import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { ArrowRight, BookOpen, Calendar, Clock, CornerDownLeft, Search, X } from 'lucide-react';
import { Modal, ModalBody } from '../../shared/ui/Modal/Modal';
import { searchGlobal, SearchCourse, SearchEvent, SearchPerson, SearchResults } from './api';
import './GlobalSearch.css';
import { PersonAvatar } from '../../shared/ui/PersonAvatar';
import { useI18n } from '../../i18n/I18nProvider';
import { formatAppDate } from '../../shared/utils/dates';
import { useOrganization } from '../organization/OrganizationContext';
import { getSidebarSections } from '../../app/layout/navigation';
import { readRecentPages } from '../../app/layout/recentPages';

const ROLE_KEYS = ['member', 'editor', 'hr', 'admin', 'super_admin'];
/** "Jump to" lists the first six places in the sidebar: search doubles as navigation. */
const JUMP_COUNT = 6;
const RECENT_COUNT = 4;

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
  /** The signed-in user, for "Jump to" and the Pages group (the sidebar's own entries). */
  user?: any;
}

interface NavPage {
  to: string;
  label: string;
  icon?: React.ElementType;
}

type ResultItem =
  | { kind: 'event'; data: SearchEvent }
  | { kind: 'course'; data: SearchCourse }
  | { kind: 'person'; data: SearchPerson }
  | { kind: 'page'; data: NavPage }
  | { kind: 'recent'; data: NavPage };

interface Group {
  key: string;
  label: string;
  items: ResultItem[];
}

const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);

/** The title with the part that matched in 500 weight (spec §5 Global search). */
function Highlight({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  const at = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span className="gsearch__match">{text.slice(at, at + q.length)}</span>
      {text.slice(at + q.length)}
    </>
  );
}

/**
 * Ctrl/⌘ K: a 640px panel near the top of the screen (spec §5). An empty query shows
 * the pages opened recently and the sidebar's places, so search is also navigation;
 * typing searches people, learning and events, and matches the sidebar's pages by name.
 */
export default function GlobalSearch({ open, onClose, user }: GlobalSearchProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const org: any = useOrganization();
  const orgEnabledModules = org?.orgEnabledModules ?? null;
  // Their role in Grow; anything else the server might send (the old "user") says nothing.
  const roleLabel = (role?: string | null) => (role && ROLE_KEYS.includes(role) ? t(`search.role.${role}`) : '');
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [recent, setRecent] = useState<NavPage[]>([]);

  // The sidebar's destinations for this person, so nothing here leads to a 403.
  const navPages: NavPage[] = useMemo(() => {
    if (!user) return [];
    const { lmsItems, exploreItems, accountItems } = getSidebarSections(user, t, orgEnabledModules);
    return [...lmsItems, ...exploreItems, ...accountItems].map((i: any) => ({ to: i.to, label: i.label, icon: i.icon }));
  }, [user, t, orgEnabledModules]);

  useEffect(() => {
    if (!open) return;
    const here = location.pathname;
    setRecent(readRecentPages().filter((p) => p.path !== here).slice(0, RECENT_COUNT).map((p) => ({ to: p.path, label: p.title })));
  }, [open, location.pathname]);

  const q = query.trim();

  // Every row in the order it is drawn, so the arrow keys follow what the eye sees.
  const groups: Group[] = useMemo(() => {
    if (!q) {
      return [
        { key: 'recent', label: t('shell.search.recent'), items: recent.map((p) => ({ kind: 'recent' as const, data: p })) },
        { key: 'jump', label: t('shell.search.jumpTo'), items: navPages.slice(0, JUMP_COUNT).map((p) => ({ kind: 'page' as const, data: p })) },
      ].filter((g) => g.items.length > 0);
    }
    if (!results) return [];
    const pages = navPages.filter((p) => p.label.toLowerCase().includes(q.toLowerCase()));
    return [
      { key: 'people', label: t('edDesign.search.people'), items: (results.people || []).map((p) => ({ kind: 'person' as const, data: p })) },
      { key: 'courses', label: t('shell.search.learning'), items: (results.courses || []).map((c) => ({ kind: 'course' as const, data: c })) },
      { key: 'events', label: t('edDesign.search.events'), items: (results.events || []).map((e) => ({ kind: 'event' as const, data: e })) },
      { key: 'pages', label: t('shell.search.pages'), items: pages.map((p) => ({ kind: 'page' as const, data: p })) },
    ].filter((g) => g.items.length > 0);
  }, [q, results, recent, navPages, t]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults(null);
      setFocusedIndex(-1);
      return undefined;
    }
    const id = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [open]);

  // Keep the row the arrow keys moved to in view.
  useEffect(() => {
    if (focusedIndex < 0) return;
    const row = resultsRef.current?.querySelector<HTMLElement>(`[data-index="${focusedIndex}"]`);
    if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex]);

  const runSearch = useCallback((value: string) => {
    if (!value.trim()) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    searchGlobal(value)
      .then((data) => {
        setResults(data);
        setFocusedIndex(-1);
      })
      .catch(() => setResults(null))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setFocusedIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), 220);
  };

  const clear = () => {
    setQuery('');
    setResults(null);
    setFocusedIndex(-1);
    inputRef.current?.focus();
  };

  const navigateTo = useCallback((item: ResultItem) => {
    onClose();
    if (item.kind === 'event') navigate(`/event/${item.data.id}`);
    else if (item.kind === 'course') navigate(`/learning/${item.data.id}`);
    else if (item.kind === 'person') navigate(`/people/${item.data.id}`);
    else navigate(item.data.to);
  }, [navigate, onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && focusedIndex >= 0 && focusedIndex < flat.length) {
      e.preventDefault();
      navigateTo(flat[focusedIndex]);
    }
  };

  const describe = (item: ResultItem): { icon: React.ReactNode; title: string; sub: string } => {
    if (item.kind === 'person') {
      const p = item.data;
      return {
        icon: <span className="gsearch__item-icon gsearch__item-icon--avatar" aria-hidden><PersonAvatar name={p.name} src={p.avatar} seed={p.id} size={28} /></span>,
        title: p.name,
        sub: [p.department, roleLabel(p.role)].filter(Boolean).join(' · ') || p.email || '',
      };
    }
    if (item.kind === 'event') {
      const ev = item.data;
      const datePart = ev.date_start ? formatAppDate(ev.date_start, { month: 'short', day: 'numeric' }) : null;
      return {
        icon: <span className="gsearch__item-icon" aria-hidden><Calendar size={16} /></span>,
        title: ev.title,
        sub: [ev.type, datePart, ev.city].filter(Boolean).join(' · '),
      };
    }
    if (item.kind === 'course') {
      const co = item.data;
      return {
        icon: <span className="gsearch__item-icon" aria-hidden><BookOpen size={16} /></span>,
        title: co.title,
        sub: [co.category, co.difficulty].filter(Boolean).join(' · '),
      };
    }
    const Icon = item.kind === 'recent' ? Clock : (item.data.icon || ArrowRight);
    return {
      icon: <span className="gsearch__item-icon" aria-hidden><Icon size={16} /></span>,
      title: item.data.label,
      sub: '',
    };
  };

  if (!open) return null;

  let index = -1;
  const noResults = !!q && !loading && !!results && flat.length === 0;
  const offers = (path: string) => navPages.some((p) => p.to === path);

  return (
    <Modal open={open} onClose={onClose} size="search" position="top">
      <ModalBody noPadding>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- the arrow keys move between the rows below the input. */}
        <div className="gsearch" onKeyDown={handleKeyDown}>
          <div className="gsearch__input-row">
            <Search size={20} className="gsearch__icon" aria-hidden="true" />
            <input
              ref={inputRef}
              className="gsearch__input"
              placeholder={t('ui.searchPlaceholder')}
              aria-label={t('shell.searchLabel')}
              value={query}
              onChange={handleChange}
              autoComplete="off"
              spellCheck={false}
            />
            {query ? (
              <button className="gsearch__clear" onClick={clear} aria-label={t('ui.clearSearch')} type="button">
                <X size={16} />
              </button>
            ) : (
              <kbd className="gsearch__kbd" aria-hidden="true">Esc</kbd>
            )}
          </div>

          <div className="gsearch__results" ref={resultsRef}>
            {!q && groups.length === 0 && <p className="gsearch__hint">{t('edDesign.search.hint')}</p>}
            {q && loading && !results && <p className="gsearch__hint">{t('edDesign.search.searching')}</p>}

            {noResults && (
              <div className="gsearch__none" role="status">
                <span className="gsearch__sr">{t('edDesign.search.noResultsTitle', { query: q })}</span>
                <p className="gsearch__none-lead" aria-hidden="true">
                  {t('shell.search.nothingFor')} <span className="gsearch__none-query">“{q}”</span>.{' '}
                  <span className="gsearch__none-muted">{t('edDesign.search.noResultsCopy')}</span>
                </p>
                <div className="gsearch__none-actions">
                  <button type="button" className="gsearch__none-btn" onClick={clear}>{t('edDesign.search.clear')}</button>
                  {offers('/people') && (
                    <Link className="gsearch__none-link" to={`/people?q=${encodeURIComponent(q)}`} onClick={onClose}>
                      {t('shell.search.inPeople')} <ArrowRight size={14} aria-hidden="true" />
                    </Link>
                  )}
                  {offers('/events') && (
                    <Link className="gsearch__none-link" to={`/events?q=${encodeURIComponent(q)}`} onClick={onClose}>
                      {t('shell.search.inEvents')} <ArrowRight size={14} aria-hidden="true" />
                    </Link>
                  )}
                </div>
              </div>
            )}

            {groups.map((group) => (
              <div key={group.key} className="gsearch__section" role="group" aria-label={group.label}>
                <div className="gsearch__section-header" aria-hidden="true">{group.label}</div>
                {group.items.map((item) => {
                  index += 1;
                  const idx = index;
                  const focused = focusedIndex === idx;
                  const { icon, title, sub } = describe(item);
                  const key = item.kind === 'page' || item.kind === 'recent' ? `${item.kind}-${item.data.to}` : `${item.kind}-${item.data.id}`;
                  return (
                    <button
                      key={key}
                      type="button"
                      data-index={idx}
                      className={`gsearch__item${focused ? ' gsearch__item--focused' : ''}`}
                      onClick={() => navigateTo(item)}
                      onMouseMove={() => { if (!focused) setFocusedIndex(idx); }}
                    >
                      {icon}
                      <span className="gsearch__item-text">
                        <span className="gsearch__item-title"><Highlight text={title} query={q} /></span>
                        {sub && <span className="gsearch__item-sub">{sub}</span>}
                      </span>
                      <CornerDownLeft size={14} className="gsearch__item-enter" aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="gsearch__footer" aria-hidden="true">
            <span className="gsearch__footer-hint"><kbd>↑</kbd><kbd>↓</kbd> {t('shell.search.move')}</span>
            <span className="gsearch__footer-hint"><kbd>↵</kbd> {t('edDesign.search.open')}</span>
            <span className="gsearch__footer-hint"><kbd>Esc</kbd> {t('edDesign.search.close')}</span>
            <span className="gsearch__footer-spacer" />
            <span className="gsearch__footer-hint"><kbd>{isMac ? '⌘K' : 'Ctrl K'}</kbd></span>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}
